import { EntityType, getDefaultAddressSchemeTypeByCountry, getEInvoiceCountryConfig } from '@components'

import { invoiceLanguageToLocale } from '../../../../../invoices/constants/invoiceLanguageToLocale'
import { CustomerType } from '../../../../enums/customerType'
import { Contact } from '../../../../types/contact'
import { ContactType } from '../../../../types/contactType'
import { CustomerForm } from '../../../../types/customerForm'
import { ShippingAddress } from '../../../../types/shippingAddress'
import { getContactPersonsWithoutPrimaryContact } from '../../../../utils/getContactPersonsWithoutPrimaryContact'

interface GetCustomerFormEditingValuesProps {
  customer: Contact
  shippingAddress?: ShippingAddress
}

export const getCustomerFormEditingValues = ({
  customer,
  shippingAddress,
}: GetCustomerFormEditingValuesProps): CustomerForm => {
  const {
    addressLine2,
    cityText,
    contactNo,
    contactPersons,
    countryId,
    currencyId,
    ean,
    id,
    invoicingLanguage,
    isAddressExempt,
    name,
    paymentTermsDays,
    phone,
    publicEntityRoutingEnabled,
    registrationNo,
    street,
    type,
    vatNo,
    zipcodeText,
  } = customer || {}

  const contactLanguage = invoicingLanguage ? invoiceLanguageToLocale[invoicingLanguage] : undefined
  const customerType = type === ContactType.Person ? CustomerType.Individual : CustomerType.Company
  const primaryContact = contactPersons?.find((contactPerson) => contactPerson.isPrimary) || contactPersons?.[0]
  const companyName = type === ContactType.Company ? name : undefined
  const entityType = publicEntityRoutingEnabled ? EntityType.Public : EntityType.Private
  const isPublicEntity = entityType === EntityType.Public

  const defaultEInvoiceScheme = getDefaultAddressSchemeTypeByCountry(isPublicEntity, countryId)
  const { isFixedScheme } = getEInvoiceCountryConfig(countryId) || {}
  // When a country mandates a single scheme (e.g. FR - SIRENE), override any stored value to avoid carrying forward stale data.
  const eInvoiceScheme = (!isFixedScheme && customer?.eInvoiceScheme) || defaultEInvoiceScheme

  const contactPersonsData =
    customerType === CustomerType.Individual
      ? getContactPersonsWithoutPrimaryContact(contactPersons)
      : (contactPersons ?? [])

  return {
    address: {
      city: cityText,
      country: countryId,
      pobox: zipcodeText,
      streetAddress1: street,
      streetAddress2: addressLine2 ?? '',
    },
    businessId: registrationNo,
    contactPersons: contactPersonsData,
    companyName,
    currency: currencyId ?? undefined,
    customerNumber: Number(contactNo) || undefined,
    customerType,
    eInvoiceAddress: { address: ean, scheme: eInvoiceScheme },
    email: primaryContact?.email,
    entityType,
    firstName: primaryContact?.firstName,
    id,
    isAddressExempt: !!isAddressExempt,
    language: contactLanguage,
    lastName: primaryContact?.lastName,
    paymentTerm: paymentTermsDays ?? undefined,
    phone,
    shippingAddress: {
      ...(shippingAddress?.id && { id: shippingAddress?.id }),
      // Convert null to undefined — modal expects strings; null prevents saving when nothing changes.
      city: shippingAddress?.city ?? undefined,
      country: shippingAddress?.countryId ?? undefined,
      pobox: shippingAddress?.zipcode ?? undefined,
      streetAddress1: shippingAddress?.addressLine1 ?? undefined,
      streetAddress2: shippingAddress?.addressLine2 ?? undefined,
    },
    vatNumber: vatNo,
  }
}
